// React
import { ReactElement } from 'react';
// Next
import { useTranslation } from 'next-i18next';
// Router
import { useRouter } from 'next/router';
// Context
import { useUser } from '@utmb/contexts/AuthContext';
// Utils
import { getGender } from '@utmb/utils/gender';
import { VALID_INDEX_THRESHOLD } from '@utmb/utils/utmb-index.constants';
// Design System
import { Flag } from '@utmb/design-system/flag/Flag';
import { Link } from '@utmb/design-system/link/link';
import { MyTable } from '@utmb/design-system/my-table/MyTable';
import { MyTableCell } from '@utmb/design-system/my-table/MyTableCell';
import { MyTableHeader } from '@utmb/design-system/my-table/MyTableHeader';
import { MyTableRow } from '@utmb/design-system/my-table/MyTableRow';
import { Pagination } from '@utmb/design-system/pagination/Pagination';
import { Skeleton } from '@utmb/design-system/placeholder/Placeholder';
// Interfaces
import { PiCategoryEnum } from '@utmb/types/enum/PiCategoryEnum';
import RaceResultPaginatedView from '@utmb/types/view/RaceResultPaginatedView';
// Styles
import styles from './race-table.module.scss';
import RaceResultView from '@utmb/types/view/RaceResultView';
import { DisplayListResult } from '@utmb/design-system/display-list-result/DisplayListResult';
import { Select } from '@utmb/design-system/select/Select';
import { ListboxOption } from '@reach/listbox';

const categories = {
    [PiCategoryEnum.GENERAL]: { text: 'General', headerClass: styles.header_general },
    [PiCategoryEnum.PI20K]: { text: '20K', headerClass: styles.header_20k },
    [PiCategoryEnum.PI50K]: { text: '50K', headerClass: styles.header_50k },
    [PiCategoryEnum.PI100K]: { text: '100K', headerClass: styles.header_100k },
    [PiCategoryEnum.PI100M]: { text: '100M', headerClass: styles.header_100m },
};

interface ResultsTableProps {
    isAuthenticated: boolean;
    results: RaceResultPaginatedView;
    piCategory: PiCategoryEnum;
    hasCotation: boolean;
    isCotationPending: boolean;
    isLoading: boolean;
    isPartialResults: boolean;
}

const RaceTable = (props: ResultsTableProps): ReactElement => {
    const { isAuthenticated, results, piCategory, hasCotation, isCotationPending, isLoading, isPartialResults } = props;
    const { offset, limit = 0, nbHits } = results;

    const { locale, push, query, pathname } = useRouter();

    const { login } = useUser();

    const { t } = useTranslation();

    // Limit could be undefined as it is defined by the API
    let currentPage = offset / limit;

    if (isNaN(currentPage)) {
        currentPage = 0;
    }

    const totalPages = Math.ceil(nbHits / limit);

    const handlePageChange = async (event: { selected: number }): Promise<void> => {
        const page = event.selected + 1;

        push({ pathname, query: { ...query, page } }, undefined, { shallow: true });
    };
    const handleGenderFilter = async (gender: string): Promise<void> => {
        push({ pathname, query: { ...query, page: 1, gender } }, undefined, { shallow: true });
    };

    const hrefBuilder = (page: number, pageCount: number) => (page >= 1 && page <= pageCount ? `/utmb-index/races/${query.race}?page=${page}` : '#');

    return (
        <section className={`${styles.rt_root} container g-0`}>
            <div className={styles.rt_table_container}>
                <DisplayListResult label={`${t('raceRanking.tableName')}${isPartialResults ? ` (${t('raceRanking.partialResults')})` : ''}`} hits={nbHits} />

                <MyTable className={styles.rt_table}>
                    <MyTableHeader className={piCategory ? categories[piCategory as PiCategoryEnum].headerClass : styles.header_general} height={65}>
                        <MyTableCell column={1}>{t('raceRanking.tableHeader.rank')}</MyTableCell>
                        <MyTableCell column={1} hiddenOnMobile>
                            {t('raceRanking.tableHeader.time')}
                        </MyTableCell>
                        <MyTableCell column={3}>{t('raceRanking.tableHeader.name')}</MyTableCell>
                        <MyTableCell column={2} hiddenOnMobile>
                            {t('raceRanking.tableHeader.nationality')}
                        </MyTableCell>
                        <MyTableCell column={1} className={styles.filter}>
                            <Select
                                id="genderFilter"
                                name="genderFilter"
                                label=""
                                firstOptionlabel={t('common.gender.label')}
                                onchange={(value: string) => {
                                    if (value === 'default') value = '';
                                    handleGenderFilter(value);
                                }}
                                value={query.gender?.toString() ?? ''}
                            >
                                <ListboxOption key={`genderFemale`} value="F" label={t(getGender('F'))}>
                                    {t(getGender('F'))}
                                </ListboxOption>
                                <ListboxOption key={`genderMale`} value="H" label={t(getGender('H'))}>
                                    {t(getGender('H'))}
                                </ListboxOption>
                            </Select>
                        </MyTableCell>
                        <MyTableCell column={2} hiddenOnMobile>
                            {t('raceRanking.tableHeader.ageGroup')}
                        </MyTableCell>
                        <MyTableCell column={2} hiddenOnMobile>
                            {t('raceRanking.tableHeader.index')}
                        </MyTableCell>
                    </MyTableHeader>

                    {results?.results?.map((result: RaceResultView, indexKey: number) => {
                        let index = result.index?.toString();

                        if (isAuthenticated) {
                            if (!hasCotation) {
                                index = t('singleRunner.resultsTable.noCotation');
                            } else if (isCotationPending) {
                                index = t('singleRunner.resultsTable.indexWaiting');
                            } else if (result.rank === null) index = '-';
                            else if (result.index && result.index <= VALID_INDEX_THRESHOLD) index = 'Finisher';
                        } else {
                            index = t('singleRunner.resultsTable.connectSeeIndex');
                        }

                        return (
                            <MyTableRow key={indexKey} height={50}>
                                <MyTableCell column={1}>{isLoading ? <Skeleton width="50%" /> : `${result.rank ?? 'DNF'}`}</MyTableCell>
                                <MyTableCell column={1} hiddenOnMobile>
                                    {isLoading ? <Skeleton width="70%" /> : `${result.time ?? '-'}`}
                                </MyTableCell>
                                <MyTableCell column={3}>
                                    {isLoading ? (
                                        <Skeleton width="50%" />
                                    ) : (
                                        <Link href={`/${locale}/runner/${result.runnerUri}`} prefetch={false}>
                                            {result.fullname}
                                        </Link>
                                    )}
                                </MyTableCell>
                                <MyTableCell column={2} hiddenOnMobile>
                                    {isLoading ? (
                                        <>
                                            <Skeleton width="15%" height="2.8rem" /> <Skeleton width="40%" />
                                        </>
                                    ) : (
                                        <>
                                            <Flag isoCode={result.nationalityCode} /> <span style={{ marginLeft: '8px' }}>{result.nationality}</span>
                                        </>
                                    )}
                                </MyTableCell>
                                <MyTableCell column={1}>
                                    {isLoading ? <Skeleton width="50%" /> : <span style={{ textTransform: 'capitalize' }}>{t(getGender(result.gender))}</span>}
                                </MyTableCell>
                                <MyTableCell column={2} hiddenOnMobile>
                                    {isLoading ? <Skeleton width="40%" /> : <>{result.ageGroup}</>}
                                </MyTableCell>
                                <MyTableCell column={2} hiddenOnMobile>
                                    {isLoading ? (
                                        <Skeleton width="60%" />
                                    ) : isAuthenticated ? (
                                        index
                                    ) : (
                                        <button className={styles.rt_login_button} onClick={login}>
                                            {index}
                                        </button>
                                    )}
                                </MyTableCell>
                            </MyTableRow>
                        );
                    })}
                </MyTable>
            </div>
            {totalPages > 1 && <Pagination forcePage={currentPage} PageCount={totalPages} onPageChange={handlePageChange} hrefBuilder={hrefBuilder} />}
        </section>
    );
};

export default RaceTable;
